/**
 * What the multiplayer screens need from the network layer. The real implementation
 * (ServerLobbyClient) wraps src/client/net/ServerConnection.ts and is registered by main.tsx;
 * integration modules may replace it with setLobbyClientFactory(). With ?mock=1 (or without
 * any factory) the UI uses MockLobbyClient.
 */
import type { ClientID, GameSettings } from "../../../core/types";
import type { LobbyInfo, LobbySummary, RatingChange } from "../../../shared/protocol";
import type { GameStartInfo } from "../../net/types";

export type ConnectionStatus = "idle" | "connecting" | "connected" | "reconnecting" | "disconnected" | "error";

export interface LobbyIdentity {
  clientID: ClientID;
  name: string;
  flag?: string;
}

export interface LobbyClientEvents {
  status: ConnectionStatus;
  lobbyList: LobbySummary[];
  lobbyState: LobbyInfo;
  /** The lobby we were in was closed (or we were kicked). */
  lobbyClosed: { reason: string; kicked?: boolean };
  /** `fromID`: the sender's public client id (servers before the lobby chat scan fix omit it). */
  lobbyChat: { from: string; text: string; at: number; fromID?: string };
  gameStart: GameStartInfo;
  /** Server error message (may be an i18n key such as "error.lobbyNotFound"). */
  error: string;
  /** Wave 4 (P15): how a finished multiplayer game was rated (Elo changes) or why not. */
  gameRated: { gameID: string; rated: boolean; reason?: string; changes?: RatingChange[] };
}

export type LobbyEventName = keyof LobbyClientEvents;

export interface LobbyClient {
  readonly isMock: boolean;
  status(): ConnectionStatus;
  /** Connect (idempotent) and identify. Identity changes are re-sent on the next connect. */
  connect(identity: LobbyIdentity): void;
  disconnect(): void;
  /** Request the public lobby list (answer arrives as a `lobbyList` event). */
  listLobbies(): void;
  createLobby(settings: GameSettings, name?: string): void;
  joinLobby(lobbyID: string): void;
  leaveLobby(): void;
  /** Host only. */
  updateLobbySettings(settings: GameSettings): void;
  setTeam(team: number): void;
  /** Host only. */
  kick(clientID: ClientID): void;
  /** Host only (private lobbies). */
  startGame(): void;
  lobbyChat(text: string): void;
  /** Wave 4 (P15): vote for option `option` of the lobby's map vote (again = change the vote). */
  voteMap?(option: number): void;
  /** Wave 4 (P15): private lobby host turns the map vote on / off. */
  setMapVote?(enabled: boolean): void;
  on<K extends LobbyEventName>(event: K, cb: (payload: LobbyClientEvents[K]) => void): () => void;
  /**
   * Stage F play test: rejoin the multiplayer game this tab was playing before a page reload
   * (remembered per tab). Connects if needed; the game arrives as a normal `gameStart`.
   * Returns whether there was a game to resume. Optional (the mock client has none).
   */
  resumeGame?(identity: LobbyIdentity): boolean;
  /** Underlying connection handed to GameSessionFactory.startMultiplayer (implementation specific). */
  getConnection(): unknown;
  /** Last measured round-trip time in ms, if known. */
  latency?(): number | null;
  /**
   * This client's id as it appears in LobbyInfo (hostClientID, LobbyPlayer.clientID). The real
   * server publishes a public id derived from the secret one; null before connecting.
   * Without this method the identity's clientID is used.
   */
  selfID?(): ClientID | null;
}

/** Small typed event emitter for LobbyClient implementations. */
export class LobbyEmitter {
  private listeners = new Map<LobbyEventName, Set<(p: unknown) => void>>();

  on<K extends LobbyEventName>(event: K, cb: (payload: LobbyClientEvents[K]) => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    const fn = cb as (p: unknown) => void;
    set.add(fn);
    return () => set?.delete(fn);
  }

  emit<K extends LobbyEventName>(event: K, payload: LobbyClientEvents[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const fn of Array.from(set)) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[lobby] listener for ${event} failed`, err);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

let factory: (() => LobbyClient) | null = null;
let instance: LobbyClient | null = null;
let mockFactory: (() => LobbyClient) | null = null;

/** Register the real lobby client factory (called by src/client/game/index.ts). */
export function setLobbyClientFactory(f: (() => LobbyClient) | null): void {
  factory = f;
  if (instance && !instance.isMock) {
    instance.disconnect();
  }
  instance = null;
}

/** Internal: the UI registers its mock here (avoids an import cycle). */
export function _setMockLobbyClientFactory(f: () => LobbyClient): void {
  mockFactory = f;
}

function mockForced(): boolean {
  try {
    return typeof location !== "undefined" && new URLSearchParams(location.search).get("mock") === "1";
  } catch {
    return false;
  }
}

/** Shared lobby client instance (real when registered, otherwise the mock). */
export function getLobbyClient(): LobbyClient {
  if (instance) return instance;
  const f = !mockForced() && factory ? factory : mockFactory;
  if (!f) throw new Error("No lobby client available");
  instance = f();
  return instance;
}
